import Phaser from 'phaser';
import Player from '../entities/Player';
import { TILE_SIZE } from '../utils/constants';
import Pathfinding from 'pathfinding';
export default class LevelPickingScene extends Phaser.Scene {
    player;
    doors;
    camera;
    cursors;
    currentLevel;
    storeZone;
    storeOpen = false;
    inventoryVisible = false;
    soundManager;
    targetPosition;
    grid;
    path;
    walls;
    constructor() {
        super('LevelPickingScene');
    }
    init(data) {
        this.currentLevel = data.level || 1;
        this.registry.set('totalScore', data.totalScore || 0);
        this.soundManager = this.registry.get('soundManager');
        let playerData = data.playerData || this.registry.get('playerData');
        if (!playerData || Object.keys(playerData.inventory).length === 0) {
            playerData = { gold: 0, inventory: {}, collectedNotes: [] };
        }
        this.registry.set('playerData', playerData);
    }
    create() {
        this.resetScenes();
        const roomWidth = 10;
        const roomHeight = 8;
        this.doors = this.physics.add.group();
        this.camera = this.cameras.main;
        this.camera.setZoom(3);
        this.camera.fadeIn(2000);
        this.soundManager.stopAllSounds();
        this.createRoom(roomWidth, roomHeight);
        this.createPlayer();
        this.createDoors();
        this.createInteriorWalls(roomWidth, roomHeight);
        this.createStoreZone(roomWidth, roomHeight);
        this.setupPlayerMovement();
        this.camera.startFollow(this.player);
        this.time.delayedCall(2000, () => {
            this.soundManager.playSound(Phaser.Math.RND.pick(['main1', 'main2', 'main3', 'main4', 'main5', 'main6', 'main7', 'main8']));
        });
        if (this.input.keyboard) {
            this.cursors = this.input.keyboard.createCursorKeys();
            this.input.keyboard.on('keydown-I', this.toggleInventory, this);
            this.input.keyboard.on('keydown-B', this.openNoteReadingScene, this);
        }
        const isMobileControlsEnabled = this.registry.get('isMobileControlsEnabled') || false;
        if (isMobileControlsEnabled) {
            this.scene.launch('JoystickScene', { player: this.player });
            this.scene.bringToTop('JoystickScene');
        }
        this.input.on('pointerdown', this.handleTouchInput, this);
        this.createGrid(roomWidth, roomHeight);
    }
    update() {
        this.handlePlayerMovement();
        this.checkStoreInteraction();
        this.updatePlayerMovementAlongPath();
    }
    handleTouchInput(pointer) {
        const gridX = Math.floor(pointer.worldX / TILE_SIZE);
        const gridY = Math.floor(pointer.worldY / TILE_SIZE);
        const pathfinder = new Pathfinding.AStarFinder();
        const gridClone = new Pathfinding.Grid(this.grid);
        const path = pathfinder.findPath(Math.floor(this.player.x / TILE_SIZE), Math.floor(this.player.y / TILE_SIZE), gridX, gridY, gridClone);
        this.path = path.map(([x, y]) => new Phaser.Math.Vector2(x * TILE_SIZE + TILE_SIZE / 2, y * TILE_SIZE + TILE_SIZE / 2));
    }
    updatePlayerMovementAlongPath() {
        if (this.path && this.path.length > 0) {
            const nextPoint = this.path[0];
            const body = this.player.body;
            const distance = Phaser.Math.Distance.Between(this.player.x, this.player.y, nextPoint.x, nextPoint.y);
            if (distance < TILE_SIZE / 4) {
                this.path.shift();
            }
            else {
                this.physics.moveToObject(this.player, nextPoint, 160);
            }
        }
    }
    createGrid(roomWidth, roomHeight) {
        this.grid = [];
        for (let y = 0; y < roomHeight; y++) {
            const row = [];
            for (let x = 0; x < roomWidth; x++) {
                const hasWall = this.walls.getChildren().some(wall => wall.x === x * TILE_SIZE + TILE_SIZE / 2 &&
                    wall.y === y * TILE_SIZE + TILE_SIZE / 2);
                row.push(hasWall ? 1 : 0);
            }
            this.grid.push(row);
        }
    }
    resetScenes() {
        this.scene.stop('MainScene');
        this.scene.stop('StoreScene');
        this.scene.stop('MinimapScene');
        const isMobileControlsEnabled = this.registry.get('isMobileControlsEnabled') || false;
        if (!isMobileControlsEnabled) {
            this.scene.stop('JoystickScene');
        }
    }
    createRoom(roomWidth, roomHeight) {
        this.walls = this.physics.add.staticGroup();
        for (let x = 0; x < roomWidth; x++) {
            for (let y = 0; y < roomHeight; y++) {
                this.add.image(x * TILE_SIZE + TILE_SIZE / 2, y * TILE_SIZE + TILE_SIZE / 2, 'floor').setOrigin(0.5).setDepth(0);
            }
        }
        this.createWalls(roomWidth, roomHeight);
    }
    createPlayer() {
        const playerData = this.registry.get('playerData');
        this.player = new Player(this, TILE_SIZE * 1.5, TILE_SIZE * 1.5, 'player', this.soundManager, playerData);
        this.player.setLevel(this.currentLevel);
        this.physics.add.existing(this.player);
        this.physics.add.collider(this.player, this.walls);
    }
    createStoreZone(roomWidth, roomHeight) {
        const storeX = roomWidth * TILE_SIZE / 2 + TILE_SIZE / 2;
        const storeY = roomHeight * TILE_SIZE / 2 + TILE_SIZE / 2;
        this.storeZone = this.physics.add.sprite(storeX, storeY, 'sealed_door').setOrigin(0.5);
        this.storeZone.setImmovable(true);
        this.physics.add.overlap(this.player, this.storeZone, this.openStore, undefined, this);
    }
    openStore() {
        if (!this.storeOpen) {
            this.storeOpen = true;
            this.scene.launch('StoreScene', { player: this.player, soundManager: this.soundManager });
            this.scene.bringToTop('StoreScene');
        }
    }
    closeStore() {
        if (this.storeOpen) {
            this.storeOpen = false;
            this.scene.stop('StoreScene');
        }
    }
    checkStoreInteraction() {
        const distance = Phaser.Math.Distance.Between(this.player.x, this.player.y, this.storeZone.x, this.storeZone.y);
        if (distance > TILE_SIZE * 1.5) {
            this.closeStore();
        }
    }
    createDoors() {
        const doorPositions = [
            { x: 2, y: 0 }, { x: 4, y: 0 }, { x: 6, y: 0 }, { x: 8, y: 0 },
            { x: 0, y: 2 }, { x: 0, y: 4 }, { x: 0, y: 6 },
            { x: 9, y: 2 }, { x: 9, y: 4 }, { x: 9, y: 6 }
        ];
        doorPositions.forEach((pos, index) => {
            const doorLevel = index + 1;
            let doorTexture = 'runed_door';
            if (doorLevel < this.currentLevel)
                doorTexture = 'open_door';
            const door = this.doors.create(pos.x * TILE_SIZE + TILE_SIZE / 2, pos.y * TILE_SIZE + TILE_SIZE / 2, doorTexture);
            door.setOrigin(0.5, 0.5)
                .setData('level', doorLevel)
                .setDepth(0);
            this.add.text(pos.x * TILE_SIZE + TILE_SIZE / 2, pos.y * TILE_SIZE - TILE_SIZE / 2, `${doorLevel}`, { fontSize: '16px', color: '#fff' }).setOrigin(0.5).setDepth(0);
        });
        this.physics.add.overlap(this.player, this.doors, (player, door) => this.enterLevel(player, door), undefined, this);
    }
    enterLevel(player, door) {
        const doorLevel = door.getData('level');
        const playerLevel = player.getLevel();
        if (doorLevel === playerLevel) {
            // Stop sounds immediately before transition
            this.soundManager.stopAllSounds(),
                this.soundManager.cleanup(),
                this.time.delayedCall(100, () => {
                    this.registry.set('currentLevel', doorLevel);
                    this.scene.start('MainScene', {
                        level: this.currentLevel,
                        totalScore: this.registry.get('totalScore'),
                        playerData: this.registry.get('playerData'),
                    });
                    this.scene.stop('LevelPickingScene');
                    this.scene.stop('JoystickScene');
                    this.scene.stop('MinimapScene');
                });
        }
    }
    setupPlayerMovement() {
        this.player.setCollideWorldBounds(true);
    }
    createWalls(roomWidth, roomHeight) {
        const doorPositions = [
            { x: 2, y: 0 }, { x: 4, y: 0 }, { x: 6, y: 0 }, { x: 8, y: 0 },
            { x: 0, y: 2 }, { x: 0, y: 4 }, { x: 0, y: 6 },
            { x: 9, y: 2 }, { x: 9, y: 4 }, { x: 9, y: 6 }
        ];
        for (let x = 0; x < roomWidth; x++) {
            for (let y = 0; y < roomHeight; y++) {
                if ((y === 0 || y === roomHeight - 1 || x === 0 || x === roomWidth - 1) &&
                    !doorPositions.some(pos => pos.x === x && pos.y === y)) {
                    this.walls.create(x * TILE_SIZE + TILE_SIZE / 2, y * TILE_SIZE + TILE_SIZE / 2, 'wall')
                        .setOrigin(0.5).setDepth(0);
                }
            }
        }
    }
    createInteriorWalls(roomWidth, roomHeight) {
        const interiorWallPositions = [
            { x: 4, y: 2 }, { x: 4, y: 3 }, { x: 4, y: 4 },
            { x: 7, y: 2 }, { x: 7, y: 3 }, { x: 7, y: 4 }
        ];
        interiorWallPositions.forEach(pos => {
            this.walls.create(pos.x * TILE_SIZE + TILE_SIZE / 2, pos.y * TILE_SIZE + TILE_SIZE / 2, 'wall')
                .setOrigin(0.5).setDepth(0);
        });
    }
    handlePlayerMovement() {
        const speed = 160;
        const body = this.player.body;
        body.setVelocity(0);
        if (this.cursors.left?.isDown || this.player.joystick?.left) {
            body.setVelocityX(-speed);
        }
        else if (this.cursors.right?.isDown || this.player.joystick?.right) {
            body.setVelocityX(speed);
        }
        if (this.cursors.up?.isDown || this.player.joystick?.up) {
            body.setVelocityY(-speed);
        }
        else if (this.cursors.down?.isDown || this.player.joystick?.down) {
            body.setVelocityY(speed);
        }
    }
    toggleInventory() {
        this.inventoryVisible = !this.inventoryVisible;
        if (this.inventoryVisible) {
            this.scene.launch('InventoryScene', {
                player: this.player,
                totalScore: this.registry.get('totalScore') || 0,
                levelScore: this.player.getScore()
            });
            this.scene.bringToTop('InventoryScene');
            this.soundManager.playSound('pageturn');
        }
        else {
            this.scene.stop('InventoryScene');
        }
    }
    openNoteReadingScene() {
        const collectedNotes = this.player.getCollectedNotes();
        if (collectedNotes?.length) {
            this.scene.launch('NoteReadingScene', { notes: collectedNotes });
            this.scene.pause('LevelPickingScene');
            this.scene.bringToTop('NoteReadingScene');
        }
    }
    shutdown() {
        this.soundManager.stopAllSounds();
        this.soundManager.cleanup();
    }
}
